import { useEffect, useState } from 'react';
import { AUTH_EXPIRED_EVENT, api, type SessionUser, type Stats } from './lib/api';
import { mailApi } from './lib/mailApi';
import { CampaignsPage } from './pages/CampaignsPage';
import { InboxPage } from './pages/InboxPage';
import { LeadsPage } from './pages/LeadsPage';
import { LoginPage } from './pages/LoginPage';
import { PipelinePage } from './pages/PipelinePage';
import { ScanPage } from './pages/ScanPage';
import { SettingsPage } from './pages/SettingsPage';

type Tab = 'leads' | 'campaigns' | 'inbox' | 'pipeline' | 'scan' | 'settings';

export default function App() {
  // undefined = still checking the session cookie.
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  useEffect(() => {
    void api.me().then(setUser).catch(() => setUser(null));
    const onExpired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  if (user === undefined) return null;
  if (user === null) return <LoginPage onSignedIn={setUser} />;
  return <Dashboard user={user} onSignedOut={() => setUser(null)} />;
}

function Dashboard({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const [tab, setTab] = useState<Tab>('leads');
  const [stats, setStats] = useState<Stats | null>(null);
  const [unreadReplies, setUnreadReplies] = useState(0);

  useEffect(() => {
    void api.stats().then(setStats).catch(() => undefined);
    void mailApi.inbox({ kind: 'reply', handled: false }).then((r) => setUnreadReplies(r.unhandledReplies)).catch(() => undefined);
  }, [tab]);

  const tiles = stats
    ? [
        { label: 'Leads', value: stats.totalLeads },
        { label: 'No website', value: stats.leadsWithoutWebsite, hot: true },
        { label: 'Hot', value: stats.hotLeads, hot: true },
        { label: 'Contactable', value: stats.outreachReadyLeads },
        { label: 'Proposals', value: stats.proposalsCreated },
        { label: 'Sent', value: stats.reportsSent },
        { label: 'Replies', value: `${stats.replies}${stats.reportsSent ? ` (${stats.replyRate}%)` : ''}` },
      ]
    : [];

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-6 px-6 py-4">
          <div className="flex items-baseline gap-3">
            <span className="text-xl font-extrabold tracking-tight text-ink">
              360<span className="text-brand">techive</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Lead Gen &amp; Site Intelligence
            </span>
          </div>

          <nav className="flex rounded-lg border border-line bg-canvas p-1">
            {(
              [
                ['leads', 'Lead finder'],
                ['campaigns', 'Campaigns'],
                ['inbox', unreadReplies ? `Inbox (${unreadReplies})` : 'Inbox'],
                ['pipeline', 'Pipeline'],
                ['scan', 'Audit / QA a URL'],
                ['settings', 'Settings'],
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${
                  tab === key ? 'bg-white text-ink shadow-sm' : 'text-slate-500 hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="ml-auto flex flex-wrap gap-5">
            {tiles.map((t) => (
              <div key={t.label} className="text-right">
                <div className={`text-lg font-extrabold leading-none ${t.hot ? 'text-poor' : 'text-ink'}`}>
                  {t.value}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{t.label}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 border-l border-line pl-5 text-sm">
            <span className="text-slate-500">{user.name ?? user.email}</span>
            <button
              className="font-semibold text-slate-500 hover:text-ink"
              onClick={() => void api.logout().finally(onSignedOut)}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {tab === 'leads' && <LeadsPage />}
      {tab === 'campaigns' && <CampaignsPage />}
      {tab === 'inbox' && <InboxPage onCountChange={setUnreadReplies} />}
      {tab === 'pipeline' && <PipelinePage />}
      {tab === 'scan' && <ScanPage />}
      {tab === 'settings' && <SettingsPage />}
    </div>
  );
}
